// Precomputed Platform map summary. Built by the publish/seed scripts and
// stored as ONE public document (meta/platformMap), so opening the map costs a
// single Firestore read. Contains only fields already public on published stories.
import type { Cloud, ContentType, Story } from "./model.ts";
import { effectiveFlag, isNew, type EffectiveFlag, type FlagStatus } from "./flags.ts";
import { PRODUCTS } from "./products.ts";
import { collapseRepeats } from "./digest.ts";

export const MAP_ITEMS_PER_PRODUCT = 10;

export interface MapItem {
  storyId: string;
  title: string;
  publishedAt: number | null;
  sortAt: number;
  contentType: ContentType;
  canonicalUrl: string;
  sourceName: string;
  estimatedReadMinutes: number;
  flag: EffectiveFlag | null;
  docsClouds: Cloud[];
  isSample: boolean;
  /** Published for local testing with the official excerpt instead of editorial summaries. */
  unreviewed: boolean;
  /** Same entry (title + day) also published on N other docs pages; counted once. */
  alsoOn?: number;
}

/** Compact per-story facts for EVERY story of a product, so tile counts and flags are exact. */
export interface StoryFact {
  p: number | null; // publishedAt
  s: FlagStatus | null; // effective flag status
  c: number; // docs clouds bitmask: aws=1, azure=2, gcp=4
  x: boolean; // isSample
  i?: string; // storyId (dedupes stories tagged with several products in one area)
}

export interface ProductSummary {
  total: number;
  items: MapItem[]; // newest first, at most MAP_ITEMS_PER_PRODUCT
  facts?: StoryFact[]; // all stories, newest first
}

const CLOUD_BIT: Record<string, number> = { aws: 1, azure: 2, gcp: 4 };
const cloudMask = (clouds: Cloud[]) => clouds.reduce((m, c) => m | (CLOUD_BIT[c] ?? 0), 0);

export interface PlatformMap {
  generatedAt: number;
  storyCount: number;
  products: Record<string, ProductSummary>;
}

export function toMapItem(s: Story): MapItem {
  return {
    storyId: s.id,
    title: s.title,
    publishedAt: s.publishedAt,
    sortAt: s.sortAt,
    contentType: s.contentType,
    canonicalUrl: s.canonicalUrl,
    sourceName: s.sourceName,
    estimatedReadMinutes: s.estimatedReadMinutes,
    flag: effectiveFlag(s),
    docsClouds: s.docsClouds,
    isSample: s.isSample,
    unreviewed: s.summaryProvenance === "source_excerpt",
  };
}

/** Deterministic: same published stories in → same map out (apart from generatedAt). */
export function buildPlatformMap(published: Story[], now = Date.now()): PlatformMap {
  const products: Record<string, ProductSummary> = {};
  for (const p of PRODUCTS) products[p.id] = { total: 0, items: [], facts: [] };
  const sorted = [...published].sort((a, b) => b.sortAt - a.sortAt || a.id.localeCompare(b.id));
  // One entry published on two docs pages counts once (board, graph, timeline agree).
  const entries = collapseRepeats(sorted);
  for (const { story: s, alsoOn } of entries) {
    for (const pid of new Set(s.products)) {
      const bucket = products[pid];
      if (!bucket) continue;
      bucket.total++;
      const item = { ...toMapItem(s), ...(alsoOn ? { alsoOn } : {}) };
      if (bucket.items.length < MAP_ITEMS_PER_PRODUCT) bucket.items.push(item);
      bucket.facts!.push({ p: s.publishedAt, s: item.flag?.status ?? null, c: cloudMask(s.docsClouds), x: s.isSample, i: s.id });
    }
  }
  return { generatedAt: now, storyCount: entries.length, products };
}

export type CloudFilter = "all" | "aws" | "azure" | "gcp";

/** Samples aren't tied to any cloud, so they stay visible under every cloud. */
export function itemMatchesCloud(i: MapItem, cloud: CloudFilter): boolean {
  return cloud === "all" || i.isSample || i.docsClouds.includes(cloud);
}

export interface TileSummary {
  count: number;
  newCount: number;
  statuses: FlagStatus[]; // distinct, in display order
  items: MapItem[];
}

const STATUS_ORDER: FlagStatus[] = ["retired", "deprecated", "ga", "public_preview", "private_preview", "beta"];

export function summarizeTile(summary: ProductSummary | undefined, cloud: CloudFilter, now = Date.now()): TileSummary {
  const items = (summary?.items ?? []).filter((i) => itemMatchesCloud(i, cloud));
  // Prefer the complete per-story facts; fall back to the (capped) items for older map documents.
  const facts: StoryFact[] = summary?.facts ?? (summary?.items ?? []).map((i) => ({ p: i.publishedAt, s: i.flag?.status ?? null, c: cloudMask(i.docsClouds), x: i.isSample }));
  const bit = cloud === "all" ? 0 : CLOUD_BIT[cloud];
  const inCloud = facts.filter((f) => cloud === "all" || f.x || (f.c & bit) !== 0);
  const present = new Set(inCloud.map((f) => f.s).filter((x): x is FlagStatus => !!x));
  return {
    count: inCloud.length,
    newCount: inCloud.filter((f) => isNew(f.p, now)).length,
    statuses: STATUS_ORDER.filter((s) => present.has(s)),
    items,
  };
}

export type MapFlagFilter = "all" | "new" | FlagStatus;

export function tileMatchesFilter(t: TileSummary, f: MapFlagFilter): boolean {
  if (f === "all") return true;
  if (f === "new") return t.newCount > 0;
  return t.statuses.includes(f);
}

export interface TimelineMonth {
  key: string; // "2026-10"
  label: string; // "Oct 2026"
  start: number; // ms, first day of month (UTC)
  items: MapItem[]; // oldest first within the month
}

/** Groups items into calendar months (UTC), oldest month first, for the timeline. */
export function groupByMonth(items: MapItem[]): TimelineMonth[] {
  const months = new Map<string, TimelineMonth>();
  for (const i of items) {
    const t = i.publishedAt ?? i.sortAt;
    const d = new Date(t);
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    if (!months.has(key)) {
      months.set(key, {
        key,
        label: d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" }),
        start: Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1),
        items: [],
      });
    }
    months.get(key)!.items.push(i);
  }
  const out = [...months.values()].sort((a, b) => a.start - b.start);
  for (const m of out) m.items.sort((a, b) => (a.publishedAt ?? a.sortAt) - (b.publishedAt ?? b.sortAt) || a.storyId.localeCompare(b.storyId));
  return out;
}

export interface WeekBucket {
  start: number; // Monday 00:00 UTC
  end: number; // exclusive
  count: number;
}

/** Two years of weekly bars; the chart scrolls sideways on narrow screens. */
export const DISTRIBUTION_MAX_WEEKS = 104;
const WEEK = 7 * 86_400_000;

export function weekStart(t: number): number {
  const d = new Date(t);
  const day = (d.getUTCDay() + 6) % 7; // Monday = 0
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day);
}

/** Publication dates of every story for a product (cloud-filtered), from the map's facts. */
export function productDates(summary: ProductSummary | undefined, cloud: CloudFilter): number[] {
  const facts: StoryFact[] = summary?.facts ?? (summary?.items ?? []).map((i) => ({ p: i.publishedAt, s: i.flag?.status ?? null, c: cloudMask(i.docsClouds), x: i.isSample }));
  const bit = cloud === "all" ? 0 : CLOUD_BIT[cloud];
  return facts.filter((f) => f.p !== null && (cloud === "all" || f.x || (f.c & bit) !== 0)).map((f) => f.p!);
}

/**
 * Weekly counts from the earliest week with an update through the current week,
 * capped to the most recent DISTRIBUTION_MAX_WEEKS. Empty weeks are kept (0).
 */
export function weeklyDistribution(dates: number[], now = Date.now()): { weeks: WeekBucket[]; olderCount: number } {
  if (!dates.length) return { weeks: [], olderCount: 0 };
  const last = weekStart(Math.max(now, ...dates));
  const first = Math.max(weekStart(Math.min(...dates)), last - (DISTRIBUTION_MAX_WEEKS - 1) * WEEK);
  const weeks: WeekBucket[] = [];
  for (let s = first; s <= last; s += WEEK) weeks.push({ start: s, end: s + WEEK, count: 0 });
  let olderCount = 0;
  for (const t of dates) {
    const i = Math.floor((weekStart(t) - first) / WEEK);
    if (i < 0) olderCount++;
    else if (i < weeks.length) weeks[i].count++;
  }
  return { weeks, olderCount };
}

/** Display-level collapse for timeline lists that mix map items with paged stories. */
export function collapseItems(items: MapItem[]): MapItem[] {
  const out = new Map<string, MapItem>();
  for (const i of items) {
    const day = new Date(i.publishedAt ?? i.sortAt).toISOString().slice(0, 10);
    const k = `${i.title.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()}|${day}`;
    const hit = out.get(k);
    if (!hit) out.set(k, { ...i });
    else if (hit.storyId !== i.storyId) hit.alsoOn = Math.max(hit.alsoOn ?? 0, i.alsoOn ?? 0, (hit.alsoOn ?? 0) + 1);
  }
  return [...out.values()];
}

/** Facts for several products, deduped by story (for area-level views). */
export function factsFor(summaries: (ProductSummary | undefined)[], cloud: CloudFilter): StoryFact[] {
  const bit = cloud === "all" ? 0 : CLOUD_BIT[cloud];
  const seen = new Set<string>();
  const out: StoryFact[] = [];
  for (const sm of summaries) {
    const facts: StoryFact[] = sm?.facts ?? (sm?.items ?? []).map((i) => ({ p: i.publishedAt, s: i.flag?.status ?? null, c: cloudMask(i.docsClouds), x: i.isSample, i: i.storyId }));
    for (const f of facts) {
      if (cloud !== "all" && !f.x && (f.c & bit) === 0) continue;
      if (f.i) {
        if (seen.has(f.i)) continue;
        seen.add(f.i);
      }
      out.push(f);
    }
  }
  return out;
}

/** Selected flag filters; an empty set means "All flags". Matching is OR across selected flags. */
export type FlagSet = ReadonlySet<"new" | FlagStatus>;

export function parseFlagParam(v: string | null): Set<"new" | FlagStatus> {
  const valid = new Set<string>(["new", "public_preview", "private_preview", "beta", "ga", "deprecated", "retired"]);
  return new Set((v ?? "").split(",").map((x) => x.trim()).filter((x): x is "new" | FlagStatus => valid.has(x)));
}

export function tileMatchesFlags(t: TileSummary, flags: FlagSet): boolean {
  if (!flags.size) return true;
  return (flags.has("new") && t.newCount > 0) || t.statuses.some((s) => flags.has(s));
}

export function itemMatchesFlags(i: MapItem, flags: FlagSet, now = Date.now()): boolean {
  if (!flags.size) return true;
  return (flags.has("new") && isNew(i.publishedAt, now)) || (!!i.flag && flags.has(i.flag.status));
}

export function factMatchesFlags(f: StoryFact, flags: FlagSet, now = Date.now()): boolean {
  if (!flags.size) return true;
  return (flags.has("new") && isNew(f.p, now)) || (!!f.s && flags.has(f.s));
}
